import { useState, useRef, useEffect } from 'react';
import {
  Plus,
  Search,
  Sparkles,
  Paperclip,
  BarChart3,
  Lightbulb,
  PenTool,
  GraduationCap,
  Target,
  ChevronDown,
  MoreHorizontal,
  Hash,
  Users,
  Bot,
  ArrowUp,
} from 'lucide-react';
import { cn } from './ui/utils';

type SidebarSection = 'agent' | 'people';

interface ChatConversation {
  id: string;
  title: string;
  preview: string;
  timestamp: string;
  isAgent: boolean;
  avatar?: string;
  handle?: string;
  unread?: number;
  online?: boolean;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

const agentConversations: ChatConversation[] = [
  { id: 'a1', title: 'Campaign performance analysis', preview: 'Here are the top 5 performing creators...', timestamp: '2m ago', isAgent: true },
  { id: 'a2', title: 'Brief 2 — Easter strategy', preview: 'I\'d recommend focusing on Reels for...', timestamp: '1h ago', isAgent: true },
  { id: 'a3', title: 'Creator outreach suggestions', preview: 'Based on your audience demographics...', timestamp: '3h ago', isAgent: true },
  { id: 'a4', title: 'Content calendar optimization', preview: 'Moving the publish date to Thursday...', timestamp: 'Yesterday', isAgent: true },
  { id: 'a5', title: 'Hashtag research for Luminara', preview: '#LuminaraGlow has 2.3K uses this week...', timestamp: 'Yesterday', isAgent: true },
  { id: 'a6', title: 'Competitor brand lift comparison', preview: 'Your brand awareness improved by 12%...', timestamp: '2 days ago', isAgent: true },
  { id: 'a7', title: 'ROI calculation help', preview: 'The estimated CPM for this campaign...', timestamp: '3 days ago', isAgent: true },
  { id: 'a8', title: 'Instagram algorithm changes', preview: 'The latest update prioritizes...', timestamp: '4 days ago', isAgent: true },
];

const peopleConversations: ChatConversation[] = [
  { id: 'p1', title: 'Amanda Axelsson', handle: '@amandaaxelsson', preview: 'Sure! I can have the draft ready by Friday', timestamp: '5m ago', isAgent: false, avatar: 'https://images.unsplash.com/photo-1678730939174-bf600a5e0af2?w=80&h=80&fit=crop&crop=face', unread: 2, online: true },
  { id: 'p2', title: 'Elin Lindqvist', handle: '@baralindqvist', preview: 'Just sent over the raw files!', timestamp: '30m ago', isAgent: false, avatar: 'https://images.unsplash.com/photo-1618590067690-2db34a87750a?w=80&h=80&fit=crop&crop=face', online: true },
  { id: 'p3', title: 'Maja Eriksson', handle: '@majaeriks', preview: 'Thanks for the feedback, updating now', timestamp: '2h ago', isAgent: false, avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&h=80&fit=crop&crop=face', online: false },
  { id: 'p4', title: 'Sofia Bergström', handle: '@sofiaberg', preview: 'The location for the shoot is confirmed', timestamp: 'Yesterday', isAgent: false, avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face', online: false },
  { id: 'p5', title: 'Oscar Nilsson', handle: '@oscarnils', preview: 'What time works for the call tomorrow?', timestamp: 'Yesterday', isAgent: false, avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face', unread: 1, online: false },
  { id: 'p6', title: 'Frida Johansson', handle: '@fridajoh', preview: 'Loved the brief! Starting today', timestamp: '2 days ago', isAgent: false, avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face', online: true },
];

const quickActions = [
  { icon: BarChart3, label: 'Analyze', color: '#be43ff' },
  { icon: Target, label: 'Strategize', color: '#00b1a5' },
  { icon: Lightbulb, label: 'Create', color: '#a9f600' },
  { icon: PenTool, label: 'Write', color: '#be43ff' },
  { icon: GraduationCap, label: 'Learn', color: '#00b1a5' },
];

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Morning';
  if (hour < 18) return 'Afternoon';
  return 'Evening';
}

export function ChatPage() {
  const [sidebarSection, setSidebarSection] = useState<SidebarSection>('agent');
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [_sidebarSearchOpen, _setSidebarSearchOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const allConversations = sidebarSection === 'agent' ? agentConversations : peopleConversations;
  const filteredConversations = searchQuery
    ? allConversations.filter(c => c.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : allConversations;

  const activeConversation = [...agentConversations, ...peopleConversations].find(c => c.id === selectedConversation);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: 'Just now',
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');

    // If it's an agent conversation or the home screen, simulate AI response
    if (!activeConversation || activeConversation.isAgent) {
      setTimeout(() => {
        const aiMsg: ChatMessage = {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: getAIResponse(userMsg.content),
          timestamp: 'Just now',
        };
        setMessages(prev => [...prev, aiMsg]);
      }, 800);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setSelectedConversation(null);
    setMessages([]);
    setInputValue('');
  };

  const handleSelectConversation = (id: string) => {
    setSelectedConversation(id);
    const conv = [...agentConversations, ...peopleConversations].find(c => c.id === id);
    if (conv?.isAgent) {
      setMessages([
        { id: 'hist-1', role: 'user', content: conv.title, timestamp: '2m ago' },
        { id: 'hist-2', role: 'assistant', content: conv.preview + '\n\nI can provide more detailed analysis if you\'d like. Just let me know what specific metrics or creators you want to explore further.', timestamp: '2m ago' },
      ]);
    } else {
      setMessages([
        { id: 'hist-1', role: 'assistant', content: `Hey! Just checking in about the campaign deliverables. How's everything going?`, timestamp: '1h ago' },
        { id: 'hist-2', role: 'user', content: conv?.preview || '', timestamp: conv?.timestamp || '' },
      ]);
    }
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)] bg-[#fafafa]">
      {/* ────────────── Sidebar ────────────── */}
      <div className="w-[280px] shrink-0 bg-white border-r-0 flex flex-col">
        {/* New chat button */}
        <div className="p-3 pb-2">
          <button
            onClick={handleNewChat}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13px] text-[#101828] hover:bg-[#f8f5ff] transition-colors border border-gray-100"
            style={{ fontWeight: 600 }}
          >
            <Plus className="w-4 h-4 text-[#be43ff]" />
            New chat
          </button>
        </div>

        {/* Section toggle */}
        <div className="px-3 pb-2">
          <div className="flex items-center bg-[#f4f4f5] rounded-lg p-[2px]">
            <button
              onClick={() => { setSidebarSection('agent'); setSearchQuery(''); }}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[12px] transition-all duration-200',
                sidebarSection === 'agent'
                  ? 'bg-white text-[#be43ff] shadow-[0_1px_2px_rgba(0,0,0,0.06)]'
                  : 'text-[#667085] hover:text-[#344054]'
              )}
              style={{ fontWeight: sidebarSection === 'agent' ? 600 : 500 }}
            >
              <Bot className="w-3.5 h-3.5" />
              Agent
            </button>
            <button
              onClick={() => { setSidebarSection('people'); setSearchQuery(''); }}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[12px] transition-all duration-200',
                sidebarSection === 'people'
                  ? 'bg-white text-[#be43ff] shadow-[0_1px_2px_rgba(0,0,0,0.06)]'
                  : 'text-[#667085] hover:text-[#344054]'
              )}
              style={{ fontWeight: sidebarSection === 'people' ? 600 : 500 }}
            >
              <Users className="w-3.5 h-3.5" />
              People
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 pb-2">
          <div className="flex items-center gap-2 bg-[#f9fafb] rounded-lg px-3 py-2 border border-gray-100">
            <Search className="w-3.5 h-3.5 text-[#98a2b3]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={sidebarSection === 'agent' ? 'Search chats...' : 'Search people...'}
              className="bg-transparent text-[12px] outline-none flex-1 placeholder:text-[#98a2b3]"
            />
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto px-2 pb-3">
          {sidebarSection === 'agent' && (
            <>
              <div className="px-2 pt-2 pb-1">
                <span className="text-[11px] text-[#98a2b3] uppercase tracking-wider" style={{ fontWeight: 600 }}>Recent</span>
              </div>
              {filteredConversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv.id)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-xl transition-all duration-150 group mb-0.5',
                    selectedConversation === conv.id
                      ? 'bg-[#f8f5ff] border border-[#be43ff]/15'
                      : 'hover:bg-[#f9fafb] border border-transparent'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'text-[13px] truncate flex-1',
                        selectedConversation === conv.id ? 'text-[#be43ff]' : 'text-[#344054]'
                      )}
                      style={{ fontWeight: 500 }}
                    >
                      {conv.title}
                    </span>
                  </div>
                  <span className="text-[11px] text-[#98a2b3] mt-0.5 block">{conv.timestamp}</span>
                </button>
              ))}
            </>
          )}

          {sidebarSection === 'people' && (
            <>
              <div className="px-2 pt-2 pb-1">
                <span className="text-[11px] text-[#98a2b3] uppercase tracking-wider" style={{ fontWeight: 600 }}>Conversations</span>
              </div>
              {filteredConversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv.id)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-xl transition-all duration-150 flex items-start gap-2.5 mb-0.5',
                    selectedConversation === conv.id
                      ? 'bg-[#f8f5ff] border border-[#be43ff]/15'
                      : 'hover:bg-[#f9fafb] border border-transparent'
                  )}
                >
                  <div className="relative shrink-0 mt-0.5">
                    <img
                      src={conv.avatar}
                      alt={conv.title}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    {conv.online && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-[#a9f600] rounded-full border-2 border-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] text-[#344054] truncate" style={{ fontWeight: 500 }}>{conv.title}</span>
                      {conv.unread && (
                        <span className="w-4.5 h-4.5 min-w-[18px] bg-[#be43ff] text-white text-[10px] rounded-full flex items-center justify-center px-1" style={{ fontWeight: 600 }}>
                          {conv.unread}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#98a2b3] truncate mt-0.5">{conv.preview}</p>
                    <span className="text-[10px] text-[#c4c9d4] mt-0.5 block">{conv.timestamp}</span>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ────────────── Main Chat Area ────────────── */}
      <div className="flex-1 flex flex-col min-w-0 relative bg-[#fafafa] rounded-tl-[24px]">
        {/* Concave corner cutout — matching platform sidebar effect */}
        <div className="absolute top-0 left-0 z-10 pointer-events-none" style={{ width: 24, height: 24 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 0H0V24C0 10.745 10.745 0 24 0Z" fill="white" />
          </svg>
        </div>
        {!selectedConversation ? (
          /* ─── Empty / Home State ─── */
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <div className="max-w-[640px] w-full flex flex-col items-center">
              {/* Greeting */}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#be43ff] to-[#8b1fd4] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-[32px] text-[#101828]" style={{ fontWeight: 300 }}>
                  {getGreeting()}, <span style={{ fontWeight: 500 }}>Noemi</span>
                </h1>
              </div>
              <p className="text-[15px] text-[#667085] mb-10">How can I help you today?</p>

              {/* Input box */}
              <div className="w-full bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden mb-5">
                <div className="px-5 pt-4 pb-2">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything about your campaigns, creators, or strategy..."
                    className="w-full text-[15px] text-[#101828] placeholder:text-[#98a2b3] outline-none resize-none bg-transparent"
                    rows={2}
                    style={{ fontWeight: 400 }}
                  />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <div className="flex items-center gap-1">
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg text-[#98a2b3] hover:text-[#667085] hover:bg-gray-50 transition-colors">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg text-[#98a2b3] hover:text-[#667085] hover:bg-gray-50 transition-colors">
                      <Hash className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="flex items-center gap-1.5 text-[12px] text-[#667085] hover:text-[#344054] transition-colors px-2 py-1 rounded-lg hover:bg-gray-50">
                      <span style={{ fontWeight: 500 }}>Beatly Agent</span>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    <button
                      onClick={handleSend}
                      disabled={!inputValue.trim()}
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200',
                        inputValue.trim()
                          ? 'bg-[#be43ff] text-white hover:bg-[#a835e0] shadow-[0_2px_8px_rgba(190,67,255,0.3)]'
                          : 'bg-gray-100 text-gray-300'
                      )}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick action pills */}
              <div className="flex items-center gap-2 flex-wrap justify-center">
                {quickActions.map((action) => (
                  <button
                    key={action.label}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 bg-white text-[13px] text-[#344054] hover:border-[#be43ff]/30 hover:bg-[#f8f5ff] transition-all duration-200"
                    style={{ fontWeight: 500 }}
                  >
                    <action.icon className="w-3.5 h-3.5" style={{ color: action.color }} />
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ─── Active Conversation ─── */
          <>
            {/* Conversation Header */}
            <div className="h-14 shrink-0 flex items-center justify-between px-5 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-3">
                {activeConversation?.isAgent ? (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#be43ff] to-[#8b1fd4] flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={activeConversation?.avatar}
                      alt={activeConversation?.title}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    {activeConversation?.online && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-[#a9f600] rounded-full border-2 border-white" />
                    )}
                  </div>
                )}
                <div>
                  <h3 className="text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>
                    {activeConversation?.isAgent ? 'Beatly Agent' : activeConversation?.title}
                  </h3>
                  {activeConversation?.isAgent ? (
                    <span className="text-[11px] text-[#a9f600]" style={{ fontWeight: 500 }}>Online</span>
                  ) : activeConversation?.handle ? (
                    <span className="text-[11px] text-[#98a2b3]">{activeConversation.handle}</span>
                  ) : null}
                </div>
              </div>
              <button className="w-8 h-8 flex items-center justify-center rounded-lg text-[#98a2b3] hover:text-[#667085] hover:bg-gray-50 transition-colors">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="max-w-[720px] mx-auto space-y-5">
                {messages.map((msg) => (
                  <div key={msg.id} className={cn('flex gap-3', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                    {msg.role === 'assistant' && (
                      activeConversation?.isAgent ? (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#be43ff] to-[#8b1fd4] flex items-center justify-center shrink-0 mt-0.5">
                          <Sparkles className="w-3.5 h-3.5 text-white" />
                        </div>
                      ) : (
                        <img
                          src={activeConversation?.avatar}
                          alt=""
                          className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5"
                        />
                      )
                    )}
                    <div
                      className={cn(
                        'max-w-[80%] rounded-2xl px-4 py-3 text-[14px]',
                        msg.role === 'user'
                          ? 'bg-[#be43ff] text-white rounded-br-md'
                          : 'bg-white border border-gray-100 text-[#344054] rounded-bl-md shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
                      )}
                      style={{ fontWeight: 400, lineHeight: '1.6' }}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Input */}
            <div className="shrink-0 px-6 pb-5 pt-2">
              <div className="max-w-[720px] mx-auto bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
                <div className="px-5 pt-3.5 pb-1.5">
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={activeConversation?.isAgent ? 'Ask the agent...' : `Message ${activeConversation?.title}...`}
                    className="w-full text-[14px] text-[#101828] placeholder:text-[#98a2b3] outline-none resize-none bg-transparent"
                    rows={1}
                    style={{ fontWeight: 400 }}
                  />
                </div>
                <div className="flex items-center justify-between px-4 pb-2.5">
                  <div className="flex items-center gap-1">
                    <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#98a2b3] hover:text-[#667085] hover:bg-gray-50 transition-colors">
                      <Paperclip className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    onClick={handleSend}
                    disabled={!inputValue.trim()}
                    className={cn(
                      'w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200',
                      inputValue.trim()
                        ? 'bg-[#be43ff] text-white hover:bg-[#a835e0] shadow-[0_2px_8px_rgba(190,67,255,0.3)]'
                        : 'bg-gray-100 text-gray-300'
                    )}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function getAIResponse(input: string): string {
  const lower = input.toLowerCase();
  if (lower.includes('campaign') || lower.includes('performance')) {
    return "Based on your current campaigns, Luminara Beauty's \"Spring Glow\" campaign is performing exceptionally well with a 3.2% engagement rate across 12 creators. The top performers are Amanda Axelsson (4.1% ER) and Elin Lindqvist (3.8% ER). Would you like me to break down the metrics by content type or creator?";
  }
  if (lower.includes('creator') || lower.includes('influencer')) {
    return "I found several creators that match your brand profile. For the Luminara Beauty aesthetic, I'd recommend looking at micro-influencers in the 15K-50K follower range in the Nordic lifestyle space. They typically deliver 2.5-4x better engagement than larger accounts. Want me to pull up a shortlist?";
  }
  if (lower.includes('brief') || lower.includes('strategy')) {
    return "For your upcoming brief, I'd suggest a Reels-first approach — Instagram's algorithm is currently boosting short-form video content by ~30%. Pair each Reel with a carousel post to maximize reach. I can draft a content framework for the creators if you'd like.";
  }
  if (lower.includes('hashtag') || lower.includes('tag')) {
    return "#LuminaraGlow is gaining traction with 2.3K weekly uses — up 18% from last week. I'd also recommend adding #CleanBeautyNordic (8.4K weekly) and #GlowRoutine (45K weekly) to your next brief. These have strong overlap with your target audience.";
  }
  return "I can help with campaign analytics, creator recommendations, content strategy, hashtag research, and more. What would you like to explore? You can also try one of the quick actions above to get started.";
}